import React, { useState, useEffect } from 'react';
import { X, Mail, Building2, User, CheckCircle2, Loader2 } from 'lucide-react';
import { subscribeToNewsletter } from '../../services/newsletterService';

const NewsletterBanner = () => {
  const [isVisible, setIsVisible] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    company: ''
  });

  // Check session storage on mount
  useEffect(() => {
    const dismissed = sessionStorage.getItem('newsletter_banner_dismissed');
    if (dismissed === 'true') {
      setIsVisible(false);
    }
  }, []);

  const handleDismiss = () => {
    sessionStorage.setItem('newsletter_banner_dismissed', 'true');
    setIsVisible(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      await subscribeToNewsletter(formData);
      setShowSuccess(true);
      setFormData({ fullName: '', email: '', company: '' });
      
      // Auto-dismiss after 3 seconds
      setTimeout(() => {
        handleDismiss();
      }, 3000);
    } catch (err) {
      setError(err?.message || 'Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isVisible) return null;

  return (
    <div className="fixed top-0 left-0 right-0 z-50 bg-gradient-to-r from-slate-900 via-green-900 to-slate-900 text-white shadow-lg">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div className="flex flex-col lg:flex-row items-center justify-between gap-4">
          {/* Left: Message */}
          <div className="flex-1 text-center lg:text-left">
            <div className="flex items-center justify-center lg:justify-start gap-2 mb-1">
              <span className="text-2xl">📊</span>
              <h3 className="text-lg font-bold">Insights Logistiques France-Côte d'Ivoire</h3>
            </div>
            <p className="text-sm text-gray-200">
              Recevez nos analyses mensuelles + accès anticipé aux nouvelles fonctionnalités pro
            </p>
          </div>

          {/* Right: Form or Success Message */}
          {showSuccess ? (
            <div className="flex items-center gap-3 bg-green-800/30 px-6 py-3 rounded-lg border border-green-600">
              <CheckCircle2 className="w-6 h-6 text-green-400" />
              <div>
                <p className="font-semibold text-green-100">Inscription réussie !</p>
                <p className="text-sm text-green-200">Merci de votre intérêt</p>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row items-center gap-2 w-full lg:w-auto">
              {/* Name Input */}
              <div className="relative w-full sm:w-auto">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  name="fullName"
                  value={formData?.fullName}
                  onChange={handleInputChange}
                  placeholder="Nom complet"
                  required
                  className="w-full sm:w-40 pl-10 pr-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
              </div>

              {/* Email Input */}
              <div className="relative w-full sm:w-auto">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="email"
                  name="email"
                  value={formData?.email}
                  onChange={handleInputChange}
                  placeholder="Email professionnel"
                  required
                  className="w-full sm:w-48 pl-10 pr-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
              </div>

              {/* Company Input */}
              <div className="relative w-full sm:w-auto">
                <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  name="company"
                  value={formData?.company}
                  onChange={handleInputChange}
                  placeholder="Entreprise"
                  required
                  className="w-full sm:w-40 pl-10 pr-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full sm:w-auto px-6 py-2 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Inscription...</span>
                  </>
                ) : (
                  <span>S'inscrire</span>
                )}
              </button>
            </form>
          )}

          {/* Close Button */}
          <button
            onClick={handleDismiss}
            className="absolute top-2 right-2 lg:relative lg:top-0 lg:right-0 p-2 hover:bg-white/10 rounded-full transition-colors duration-200"
            aria-label="Fermer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mt-2 text-center lg:text-right">
            <p className="text-sm text-red-300">{error}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default NewsletterBanner;